import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { DenialList } from 'models/DenialList';
import { destroyAdminDenialList } from 'services/api/admin/denialLists';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteDenialListModalProps = {
  denialList: DenialList;
  onClose: () => void;
};

// Admin "Delete Denial List" confirmation. Soft-deletes (archives) the whole list. Archiving frees
// the client to be picked again in the "Add New List" dropdown, so both the lists table and the
// eligible-clients query are invalidated on success.
export default function DeleteDenialListModal({ denialList, onClose }: DeleteDenialListModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: () => destroyAdminDenialList(denialList.id),
  });

  const handleDelete = () => {
    mutate(undefined, {
      onSuccess: () => {
        invalidateQueries({ adminDenialLists: true, denialListEligibleClients: true });
        addToast({ text: `${denialList.client?.name ?? 'Client'}’s denial list deleted` });
        onClose();
      },
    });
  };

  return (
    <StandardModal title='Delete Denial List?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <Content>
        Deleting {denialList.client?.name ?? 'this client'}&rsquo;s denial list removes it entirely. Patients on it will
        no longer be automatically flagged on matching referrals.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleDelete} loading={isPending} disabled={isPending}>
          Delete Denial List
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
